<script lang="ts">
import type { ListingView } from '#layers/listing/types/listing';
import type { OverflowTabItem, TabsItem } from '#layers/ui/public';
import type { ListingLayoutOption } from './types';

interface ListingViewsProps {
	views: ListingView[];
	defaultView: ListingView;
	resolveLayoutOption: (layoutId: string) => ListingLayoutOption | undefined;
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { randomUuid } from '#layers/base/public';
import { useListingViews } from '#layers/listing/composables/use-listing-views';
import { ListCard, showConfirmationDialog, useCursorAnchor } from '#layers/ui/public';

const props = defineProps<ListingViewsProps>();

const emits = defineEmits<{
	add: [view: ListingView['id']];
	edit: [view: ListingView['id']];
	duplicate: [view: ListingView['id']];
	deleteView: [view: ListingView['id']];
}>();

const appConfig = useAppConfig();
const { t } = useI18n();
const current = defineModel<string>('currentViewId');

const { getViewName } = useListingViews({
	collectionId: null,
	resolveLayoutName: (layoutId) => props.resolveLayoutOption(layoutId)?.name || layoutId,
});

// Combine default view with user views
const allViews = computed(() => [props.defaultView, ...props.views]);

const items = computed<TabsItem[]>(() => allViews.value.map((view) => {
	const layoutOption = props.resolveLayoutOption(view.layoutId);
	return {
		value: view.id,
		label: getViewName(view),
		icon: view.icon ?? layoutOption?.icon ?? undefined,
	};
}));

// The collapsed subset is slot-scoped, so the current view's highlight is added per render rather than in a computed.
function withActiveHighlight(views: OverflowTabItem[]) {
	return views.map((view) => ({ ...view, class: view.active ? 'bg-muted!' : undefined }));
}

function createView() {
	const newViewId: ListingView['id'] = randomUuid();
	emits('add', newViewId);
	current.value = newViewId;
}

async function handleDelete(viewId: string) {
	const view = allViews.value.find((v) => v.id === viewId);
	if (!view) return;

	const viewName = getViewName(view);

	const confirmed = await showConfirmationDialog({
		props: {
			title: t('listing.views.delete.question', { name: viewName }),
			confirm: { color: 'error', label: t('listing.views.delete.confirm') },
		},
	});

	if (confirmed) {
		emits('deleteView', viewId);
	}
}

function getContextMenuItems(viewId: string) {
	const isDefaultView = viewId === props.defaultView.id;

	return [
		{
			label: t('common.edit'),
			icon: 'lucide:pencil',
			onSelect: () => emits('edit', viewId),
		},
		{
			label: t('common.duplicate'),
			icon: 'lucide:copy',
			onSelect: () => emits('duplicate', viewId),
		},
		{
			label: t('common.delete'),
			icon: 'lucide:trash',
			color: 'error' as const,
			disabled: isDefaultView,
			onSelect: () => handleDelete(viewId),
		},
	];
}

const contextMenuOpen = ref(false);
const contextMenuViewId = ref<string | null>(null);
const { reference: virtualReference, anchorTo: anchorContextMenu } = useCursorAnchor();

const currentContextMenuItems = computed(() => {
	if (!contextMenuViewId.value) return [];
	return getContextMenuItems(contextMenuViewId.value);
});

function openContextMenu(viewId: string, event: MouseEvent) {
	event.preventDefault();
	event.stopPropagation();

	contextMenuViewId.value = viewId;
	anchorContextMenu(event);
	contextMenuOpen.value = true;
}

// Handle right-click on tabs
function handleTabContextMenu(event: MouseEvent) {
	const tabButton = (event.target as HTMLElement).closest('[role="tab"]');
	const viewId = tabButton?.getAttribute('id')?.split('-trigger-')[1];

	if (!viewId) return;

	openContextMenu(viewId, event);
}

function handleContextMenuClose(open: boolean) {
	if (!open) {
		contextMenuViewId.value = null;
	}
}
</script>

<template>
	<div class="me-1 flex min-w-18 flex-1 items-center">
		<DTabs
			v-model="current"
			:items
			:min-visible-tabs="0"
			:ui="{ root: 'gap-0', label: 'max-w-40 truncate' }"
			size="sm"
			collapsible
			@contextmenu="handleTabContextMenu"
		>
			<template #list-trailing="{ hasOverflow }">
				<DTooltip v-if="!hasOverflow" :text="$t('listing.views.add_new')">
					<DButton
						variant="ghost"
						color="neutral"
						:icon="appConfig.ui.icons.plus"
						:aria-label="$t('listing.views.add_new')"
						size="sm"
						square
						data-testid="listing-views-add-button"
						@click="createView"
					/>
				</DTooltip>
			</template>

			<template #overflow="{ items: overflowViews, count, select, close }">
				<ListCard
					:items="withActiveHighlight(overflowViews)"
					selectable
					size="sm"
					class="w-55 border-none"
					data-testid="listing-views-overflow"
					:ui="{
						item: {
							content: 'gap-x-2 text-xs',
							base: 'cursor-pointer bg-default! px-3.5 py-2 after:hidden hover:bg-muted!',
							label: 'truncate text-xs',
						},
						headerWrapper: 'px-3.5 py-2.5',
						header: 'h-auto text-xs text-muted',
					}"
					@select="(item) => select(item.id)"
				>
					<template #header>
						{{ $t('listing.views.count', count) }}
					</template>
					<template #header-actions>
						<DButton
							variant="ghost"
							color="neutral"
							size="sm"
							:icon="appConfig.ui.icons.plus"
							:label="$t('listing.views.new_view')"
							@click="createView(); close()"
						/>
					</template>
					<template #item-leading="{ item }">
						<DIcon v-if="item.icon" :name="item.icon" size="16" />
					</template>
				</ListCard>
			</template>
		</DTabs>
	</div>
	<DDropdownMenu
		v-model:open="contextMenuOpen"
		:items="currentContextMenuItems"
		:content="{ reference: virtualReference }"
		size="sm"
		@update:open="handleContextMenuClose"
	/>
</template>
